import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { activityService } from '../../../services/activityService';

const ACTIVITY_CONFIG = {
  task_completed: { icon: 'CheckCircle2', color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'Task Completed' },
  snag_created: { icon: 'AlertOctagon', color: 'text-amber-600', bg: 'bg-amber-100', label: 'Snag Raised' },
  snag_resolved: { icon: 'CheckSquare', color: 'text-blue-600', bg: 'bg-blue-100', label: 'Snag Resolved' },
  snag_updated: { icon: 'Edit3', color: 'text-slate-600', bg: 'bg-slate-100', label: 'Snag Updated' },
  incident_created: { icon: 'ShieldAlert', color: 'text-red-600', bg: 'bg-red-100', label: 'Incident Reported' },
  document_uploaded: { icon: 'FileUp', color: 'text-purple-600', bg: 'bg-purple-100', label: 'Document Uploaded' },
  document_expiring: { icon: 'Clock', color: 'text-orange-600', bg: 'bg-orange-100', label: 'Document Expiring' },
  site_created: { icon: 'Building2', color: 'text-accent', bg: 'bg-accent/10', label: 'Site Created' },
  worker_assigned: { icon: 'UserPlus', color: 'text-teal-600', bg: 'bg-teal-100', label: 'Worker Assigned' },
  default: { icon: 'Activity', color: 'text-muted-foreground', bg: 'bg-muted', label: 'Activity' }
};

const formatRelativeTime = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const ActivityFeed = ({ activities: propActivities }) => {
  const navigate = useNavigate();
  const [liveActivities, setLiveActivities] = useState([]);
  const [newIds, setNewIds] = useState(new Set());
  const unsubscribeRef = useRef(null);

  // Seed from prop activities on mount / prop change
  useEffect(() => {
    if (propActivities?.length > 0) {
      setLiveActivities(propActivities);
    }
  }, [propActivities]);

  // Real-time subscription for all company activities
  useEffect(() => {
    if (unsubscribeRef?.current) unsubscribeRef?.current();

    const unsubscribe = activityService?.subscribeToAllActivities((newActivity) => {
      setLiveActivities(prev => {
        if (prev?.some(a => a?.id === newActivity?.id)) return prev;
        return [newActivity, ...prev]?.slice(0, 50);
      });
      setNewIds(prev => new Set([...prev, newActivity.id]));
      setTimeout(() => {
        setNewIds(prev => {
          const next = new Set(prev);
          next?.delete(newActivity?.id);
          return next;
        });
      }, 5000);
    });

    unsubscribeRef.current = unsubscribe;
    return () => {
      if (unsubscribeRef?.current) {
        unsubscribeRef?.current();
        unsubscribeRef.current = null;
      }
    };
  }, []);

  const handleActivityClick = (activity) => {
    const type = activity?.activity_type || activity?.type;
    switch (type) {
      case 'document_uploaded':
      case 'document_expiring': navigate('/document-management');
        break;
      case 'incident_created':
        if (activity?.site_id) navigate(`/site-detail-dashboard/${activity?.site_id}?tab=incidents`);
        else navigate('/incident-reporting');
        break;
      case 'snag_created': case'snag_updated': case'snag_resolved':
        if (activity?.site_id) navigate(`/site-detail-dashboard/${activity?.site_id}?tab=snags`);
        else navigate('/snags-list-creation');
        break;
      case 'task_completed':
        if (activity?.site_id) navigate(`/site-detail-dashboard/${activity?.site_id}?tab=tasks`);
        break;
      case 'site_created':
        if (activity?.site_id) navigate(`/site-detail-dashboard/${activity?.site_id}`);
        else navigate('/admin-dashboard');
        break;
      default:
        break;
    }
  };

  const displayActivities = liveActivities?.length > 0 ? liveActivities : (propActivities || []);

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 border border-border p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-foreground font-heading">Recent Activity</h3>
          <span className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Live
          </span>
        </div>
        <Icon name="Activity" size={20} color="var(--color-muted-foreground)" />
      </div>
      <div className="space-y-3">
        {displayActivities?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Icon name="Activity" size={48} className="text-muted-foreground mb-3" />
            <p className="text-muted-foreground text-center">No recent activity</p>
          </div>
        ) : (
          displayActivities?.slice(0, 20)?.map((activity) => {
            const type = activity?.activity_type || activity?.type;
            const cfg = ACTIVITY_CONFIG?.[type] || ACTIVITY_CONFIG?.default;
            const isNew = newIds?.has(activity?.id);
            const isClickable = ['document_uploaded', 'document_expiring', 'incident_created',
              'snag_created', 'snag_updated', 'snag_resolved', 'task_completed', 'site_created']?.includes(type);
            const userName = activity?.user?.full_name || null;
            const siteName = activity?.site?.site_name || null;

            return (
              <div
                key={activity?.id}
                className={`flex gap-3 rounded-lg transition-all ${
                  isClickable ? 'cursor-pointer hover:bg-muted/50 p-2 -mx-2' : 'p-2 -mx-2'
                } ${isNew ? 'bg-accent/5 ring-1 ring-accent/20' : ''}`}
                onClick={() => isClickable && handleActivityClick(activity)}
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onKeyDown={(e) => {
                  if (isClickable && (e?.key === 'Enter' || e?.key === ' ')) {
                    e?.preventDefault();
                    handleActivityClick(activity);
                  }
                }}
              >
                <div className="flex-shrink-0 mt-0.5">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full ${cfg?.bg}`}>
                    <Icon name={cfg?.icon} size={15} className={cfg?.color} />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-1">
                    <p className="text-sm text-foreground font-medium leading-snug">{activity?.title}</p>
                    {isNew && <span className="flex-shrink-0 w-2 h-2 rounded-full bg-accent mt-1" />}
                  </div>
                  {activity?.description && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{activity?.description}</p>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-xs text-muted-foreground">
                      {activity?.timestamp || formatRelativeTime(activity?.created_at)}
                    </p>
                    {userName && (
                      <span className="text-xs text-muted-foreground/60">· {userName}</span>
                    )}
                    {siteName && (
                      <span className="text-xs text-muted-foreground/60">· {siteName}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ActivityFeed;